import React from 'react';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import { shareViaFacebook, shareViaPinterest, shareViaTwitter } from '../../utils/shareHelpers';

import PropTypes from 'prop-types';
import ShareIcon from 'dibs-vg/dist/react/share';
import EnvelopeIcon from 'dibs-vg/dist/react/envelope';
import FacebookIcon from 'dibs-vg/dist/react/facebook';
import PinterestIcon from 'dibs-vg/dist/react/pinterest';
import TwitterIcon from 'dibs-vg/dist/react/twitter';

import styles from './styles/StorefrontSocialShare.scss';
import classnames from 'classnames';

const ENVELOPE = 'envelope';
const FACEBOOK = 'facebook';
const PINTEREST = 'pinterest';
const TWITTER = 'twitter';

const ICONS = {};
ICONS[ENVELOPE] = EnvelopeIcon;
ICONS[FACEBOOK] = FacebookIcon;
ICONS[PINTEREST] = PinterestIcon;
ICONS[TWITTER] = TwitterIcon;

const messages = defineMessages({
    subject: {
        id: 'StorefrontSocialShare.emailSubject',
        defaultMessage: '{company} on 1stdibs',
    },
    body: {
        id: 'StorefrontSocialShare.emailBody',
        defaultMessage: '"See more amazing pieces from {company} on 1stdibs. {url}',
    },
    text: {
        id: 'StorefrontSocialShare.text',
        defaultMessage: 'See more amazing pieces from {company} on 1stdibs.',
    },
});

/**
 * @class StorefrontSocialShare
 * Has 3 states
 *  - Closed
 *  - Open with icons showing
 *  - Open with Thank You message showing
 */
class StorefrontSocialShare extends React.Component {
    constructor(props) {
        super(props);

        this._onClickShare = this._onClickShare.bind(this);
        this.closeComponent = this.closeComponent.bind(this);

        this.state = {
            isOpen: false, // is the component open or closed
            showThanks: false, // show the thank you message
        };
    }

    componentDidMount() {
        window.addEventListener('scroll', this.closeComponent);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.closeComponent);
    }

    /**
     * Closes the component if it is open
     */
    closeComponent() {
        if (this.state.isOpen) {
            this.setState({
                isOpen: false,
                showThanks: false,
            });
        }
    }

    /**
     * Opens the component if it is closed
     */
    openComponent() {
        if (!this.state.isOpen) {
            this.setState({
                isOpen: true,
                showThanks: false,
            });

            if (this.props.onOpenSocialShare) {
                this.props.onOpenSocialShare();
            }
        }
    }

    /**
     * Shows the thank you message and then closes the component
     */
    showThanks() {
        if (this.state.isOpen && !this.state.showThanks) {
            this.setState({ showThanks: true });
        }
    }

    /**
     * Convenience method to show Thank You Message for 1 second and then close the component
     */
    showThanksAndClose() {
        this.showThanks();
        setTimeout(() => this.closeComponent(), 1000);
    }

    /**
     * Handler for when share icon is clicked
     *  - sets the state to open with icons showing
     *  - sets the state to closed
     */
    _onClickShare(e) {
        e.preventDefault();
        if (!this.props.disable) {
            if (this.state.isOpen) {
                this.closeComponent();
            } else {
                this.openComponent();
            }
        }
    }

    /**
     * Handler for when social icon is clicked
     */
    _onClickSocialIcon(socialIcon, e) {
        e.preventDefault();
        const company = get(this.props, 'seller.sellerProfile.company');
        const url = window.location.href;
        const text = this.props.intl.formatMessage(messages.text, { company });

        switch (socialIcon) {
            case ENVELOPE: {
                const subject = encodeURIComponent(
                    this.props.intl.formatMessage(messages.subject, { company })
                );
                const body = encodeURIComponent(
                    this.props.intl.formatMessage(messages.body, { company, url })
                );
                window.location.href = `mailto:?subject=${subject}&body=${body}`;
                break;
            }
            case FACEBOOK:
                shareViaFacebook();
                break;
            case TWITTER:
                shareViaTwitter({ text, url });
                break;
            case PINTEREST:
                shareViaPinterest({ description: text, url });
                break;
        }

        this.showThanksAndClose();
    }

    _getIcon(iconKey) {
        return ICONS[iconKey] || null;
    }

    /**
     * Renders the social icons
     */
    _renderSocialIcons() {
        const className = classnames(styles.socialWrapper, 'rowFlex');
        const socialButtonClass = classnames(styles.button, styles.social, 'colXs3');
        const thankYouMessageClass = classnames(styles.thankYouMessage, 'colXs12');

        return (
            <div ref="socialWrapper" className={className}>
                {[ENVELOPE, FACEBOOK, PINTEREST, TWITTER].map(icon => {
                    const Icon = this._getIcon(icon);
                    return (
                        <a
                            key={icon}
                            ref={icon}
                            className={socialButtonClass}
                            onClick={this._onClickSocialIcon.bind(this, icon)}
                        >
                            <Icon className={styles.socialSvg} />
                        </a>
                    );
                })}
                <div className={thankYouMessageClass}>
                    <FormattedMessage
                        id="StorefrontSocialShare.thankYou"
                        defaultMessage="THANKS!"
                    />
                </div>
            </div>
        );
    }

    render() {
        const className = classnames(styles.container, {
            [styles.isOpen]: this.state.isOpen,
            [styles.showThanks]: this.state.showThanks,
        });

        return (
            <div ref="container" className={className}>
                <div ref="wrapper" className={styles.wrapper}>
                    {/* Social Icons */}
                    {this._renderSocialIcons()}

                    {/* Share Icon */}
                    <a
                        ref="share"
                        className={`${styles.button} ${styles.share}`}
                        onClick={this._onClickShare}
                        href="#"
                    >
                        <ShareIcon />
                    </a>
                </div>
            </div>
        );
    }
}

StorefrontSocialShare.propTypes = {
    disable: PropTypes.bool,
    seller: PropTypes.object.isRequired,
};

export default createFragmentContainer(injectIntl(StorefrontSocialShare), {
    seller: graphql`
        fragment StorefrontSocialShare_seller on Seller {
            sellerProfile {
                company
            }
        }
    `,
});
